import { Component, OnInit, OnDestroy } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { AdminSettingsService } from '../../../servicies/admin-settings.service';
import { WebStorageService } from 'src/app/shared/services/web-storage.service';
import { DialogService } from 'primeng/dynamicdialog';
import { AppointmentService } from '../../../servicies/appointment.service';
import { BaseComponent } from 'src/app/shared/components/base.component';
import { forkJoin, Subject } from 'rxjs';
import { SelfSchedulingConfigService } from 'src/app/admin/servicies/self-scheduling-config-service';
import { PaymentIntegrationFormModel } from 'src/app/interfaces/payment-integration';
import { SettingsRequestModel } from 'src/app/shared/models/request/settings/settings-request-model';
import { AmountToCollectMode, PatientPaymentMode } from 'src/app/interfaces/payment-enums';
import { SettingsResponseModel } from 'src/app/shared/models/response/appointment/settings-response';
import { UpdatePaymentSettingsRequest } from 'src/app/interfaces/update-payment-settings-request';

@Component({
  selector: 'app-payment-integration',
  templateUrl: './payment-integration.component.html',
  styleUrls: ['./payment-integration.component.sass'],
  providers: [DialogService],
  standalone: false
})
export class PaymentIntegrationComponent extends BaseComponent implements OnInit, OnDestroy {
  form: any;
  loading = false;
  unsubscriber = new Subject<boolean>();
  classificationOptions: { value: number; name: string }[];
  insuranceOptions: { value: number; name: string }[];
  paymentIntegrationSetting: any;
  paymentConfig: PaymentIntegrationFormModel | null = null;
  loggedInUserId: number;

  amountToCollectModeOptions = [
    { name: 'Strict (No Edit Allowed)', code: AmountToCollectMode.STRICT },
    { name: 'Flexible (Edit Allowed)', code: AmountToCollectMode.FLEXIBLE }
  ];

  patientPaymentModes = [
    { name: 'No Payment', code: PatientPaymentMode.NO_PAYMENT },
    { name: 'Collect Payment (Consent Optional)', code: PatientPaymentMode.COLLECT_PAYMENT },
    { name: 'Collect Payment & Save Card', code: PatientPaymentMode.COLLECT_AND_SAVE_CARD },
    { name: 'Save Card', code: PatientPaymentMode.SAVE_CARD }
  ];
  public PatientPaymentMode = PatientPaymentMode;

  constructor(
    private webStorageService: WebStorageService,
    private formBuilder: UntypedFormBuilder,
    private settingsService: AdminSettingsService,
    messageService: MessageService,
    private configService: SelfSchedulingConfigService
  ) {
    super(messageService);

    const loggedInUser = this.webStorageService.getCurrentUserToken();
    this.loggedInUserId = loggedInUser ? loggedInUser.userId : 0;
  }

  ngOnDestroy(): void {
    this.unsubscriber.next(true);
    this.unsubscriber.complete();
  }

  ngOnInit() {
    this.loadData();

    this.form = this.formBuilder.group({
      enablePaymentIntegration: [null, Validators.required],
      fixAmount: ['', [Validators.required]],
      amountToCollectSource: [null],
      patientPortalSource: [null],
      enableAutoPayConsent: [null],
      autoPayConsentText: [''],
      excludeInsurance: [null],
      excludeClassification: [null],
      enableCancellationFee: [null],
      cancellationFeeAmount: [''],
      cancellationWindow: [''],
      cancellationConsentText: ['']
    });
  }

  private loadData() {
    const classificationType$ = this.settingsService.GetClassificationTypes();
    const insuranceList$ = this.configService.GetAllInsurance();
    const settings$ = this.settingsService.getAllSettings();

    forkJoin([classificationType$, insuranceList$, settings$]).subscribe(
      (results) => {
        const [classificationTypeResult, insuranceList, settingsResult] = results;

        this.processResult<any>(classificationTypeResult, () => {
          this.classificationOptions = classificationTypeResult.responseModel.map((item) => ({
            value: item.crmClassificationTypeId,
            name: item.crmClassificationDisplayName
          }));
        });

        this.processResult<any>(insuranceList, () => {
          this.insuranceOptions = insuranceList.responseModel.map((item) => ({
            value: item.insuranceProviderName,
            name: item.insuranceProviderName
          }));
        });

        this.processResult<any>(settingsResult, () => {
          const settingsArray = settingsResult.responseModel as unknown as any[];
          this.paymentIntegrationSetting = settingsArray.find((setting: any) => setting.schSettingKey === 'PaymentIntegration');

          if (this.paymentIntegrationSetting && this.paymentIntegrationSetting.schSettingValue) {
            this.paymentConfig = this.parsePaymentSettingValue(this.paymentIntegrationSetting.schSettingValue);
          }
        });

        this.loading = false;
      },
      (error) => {
        console.error('Error loading data', error);
        this.loading = false;
      }
    );
  }

  private parsePaymentSettingValue(jsonString: string): PaymentIntegrationFormModel | null {
    try {
      const parsedData = JSON.parse(jsonString);

      const config: PaymentIntegrationFormModel = {
        enablePaymentIntegration: parsedData.enablePaymentIntegration || false,
        fixAmount: parsedData.fixAmount ? parsedData.fixAmount.toString() : '',
        amountToCollectSource: parsedData.amountToCollectSource || null,
        patientPortalSource: parsedData.patientPortalSource || null,
        enableAutoPayConsent: parsedData.enableAutoPayConsent || false,
        autoPayConsentText: parsedData.autoPayConsentText || '',
        excludeInsurance: this.parseToArray(parsedData.excludeInsurance),
        excludeClassification: this.parseToArray(parsedData.excludeClassification),
        enableCancellationFee: parsedData.enableCancellationFee || false,
        cancellationFeeAmount: parsedData.cancellationFeeAmount ? parsedData.cancellationFeeAmount.toString() : '',
        cancellationWindow: parsedData.cancellationWindow ? parsedData.cancellationWindow.toString() : '',
        cancellationConsentText: parsedData.cancellationConsentText || ''
      };

      this.populateForm(config);
      return config;
    } catch (error) {
      console.error('Error parsing payment integration settings:', error);
      return null;
    }
  }

  private parseToArray(value: any): number[] {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'number') {
      return [value];
    }
    if (typeof value === 'string' && value.trim()) {
      return value
        .split(',')
        .map((item) => parseInt(item.trim()))
        .filter((num) => !isNaN(num));
    }
    return [];
  }

  private populateForm(config: PaymentIntegrationFormModel): void {
    if (config) {
      console.log('Populating form with config:', config);
      console.log('excludeInsurance array:', config.excludeInsurance);
      console.log('excludeClassification array:', config.excludeClassification);
      console.log('Available insurance options:', this.insuranceOptions);
      console.log('Available classification options:', this.classificationOptions);

      this.form.patchValue({
        enablePaymentIntegration: config.enablePaymentIntegration ? 'True' : 'False',
        fixAmount: config.fixAmount,
        amountToCollectSource: config.amountToCollectSource,
        patientPortalSource: config.patientPortalSource,
        enableAutoPayConsent: config.enableAutoPayConsent ? 'True' : 'False',
        autoPayConsentText: config.autoPayConsentText,
        excludeInsurance: config.excludeInsurance,
        excludeClassification: config.excludeClassification,
        enableCancellationFee: config.enableCancellationFee ? 'True' : 'False',
        cancellationFeeAmount: config.cancellationFeeAmount,
        cancellationWindow: config.cancellationWindow,
        cancellationConsentText: config.cancellationConsentText
      });

      console.log('Form populated. Current form values:', this.form.value);
    }
  }

  onSubmit(): void {
    if (this.form.valid) {
      this.loading = true;
      var request = this.getValuesFromForm();

      this.settingsService.updatePaymentSettings(request).subscribe({
        next: () => {
          this.loading = false;
          this.showSuccessMessage('Configuration saved successfully!');
        },
        error: (error) => {
          this.loading = false;
          console.error('Error saving configuration:', error);
          this.showErrorMessage('Failed to save configuration');
        }
      });
    } else {
      this.form.markAllAsTouched();
      this.showWarningMessage('Please fill in all required fields');
    }
  }

  private getValuesFromForm(): UpdatePaymentSettingsRequest {
    const formValue = this.form.value;

    const configToSave: UpdatePaymentSettingsRequest = {
      enablePaymentIntegration: formValue.enablePaymentIntegration === 'True',
      fixAmount: parseFloat(formValue.fixAmount) || 0,
      amountToCollectSource: formValue.amountToCollectSource,
      patientPortalSource: formValue.patientPortalSource,
      enableAutoPayConsent: formValue.enableAutoPayConsent === 'True',
      autoPayConsentText: formValue.autoPayConsentText || '',
      excludeInsurance: formValue.excludeInsurance || [],
      excludeClassification: formValue.excludeClassification || [],
      enableCancellationFee: formValue.enableCancellationFee === 'True',
      cancellationFeeAmount: formValue.cancellationFeeAmount || '',
      cancellationWindow: formValue.cancellationWindow || '',
      cancellationConsentText: formValue.cancellationConsentText || '',
      modifiedByUserInfoId: this.loggedInUserId
    };

    return configToSave;
  }
}
